<template>
  <div>
    <div class="d-flex align-items-center justify-content-end mb-3">
      <div>
        <b-button
          v-show="chartData"
          v-b-toggle.collapse-chart-sample
          variant="teal"
          class="mr-2"
          @click="viewSample = !viewSample"
        >
          <b-icon-bar-chart-fill />
          샘플 보기
        </b-button>

        <b-button variant="primary" class="mr-2" :disabled="!chartData" @click="initChart">
          <b-icon-arrow-clockwise />
          리셋하기
        </b-button>
      </div>
    </div>
    <template v-if="chartData">
      <b-collapse id="collapse-chart-sample" class="mt-2 mb-3">
        <b-card>
          <p class="card-text">샘플을 적용해보세요</p>
          <b-button
            v-for="(chart_data, idx) in data_sample_list"
            :key="idx"
            size="sm"
            class="mr-2 mb-2"
            variant="outline-dark"
            @click="applySample(idx)"
          >
            {{ chart_data.label }}
          </b-button>
        </b-card>
      </b-collapse>
    </template>

    <template v-if="!chartData">
      <b-card header="차트 생성/편집">
        <div class="text-center p-3">
          <b-btn variant="primary" class="mr-3" @click="initChart">신규 생성하기</b-btn>
          <b-btn v-show="!chartData" variant="teal" @click="viewSample = !viewSample"
            >샘플로 시작하기
          </b-btn>
        </div>

        <b-collapse id="collapse-chart-sample" v-model="viewSample" class="mt-3">
          <b-card>
            <p class="card-text text-center fw-500">샘플을 적용해보세요!</p>
            <b-button
              v-for="(chart_data, idx) in data_sample_list"
              :key="`chart-sample-${idx}`"
              size="sm"
              class="mr-2 mb-2"
              variant="outline-dark"
              @click="applySample(idx)"
            >
              {{ chart_data.label }}
            </b-button>
          </b-card>
        </b-collapse>
      </b-card>
    </template>

    <template v-else>
      <canvas
        ref="myChart"
        :style="'max-height: ' + chartData?.options?.chart_height + 'px;'"
        class="border border-light shadow-sm rounded"
      ></canvas>
      <small
        v-if="chartData && chartData.options"
        class="text-muted p-4"
        style="white-space: pre-line"
      >
        {{ chartData.options.footnote }}
      </small>

      <b-card no-body class="mt-3">
        <b-tabs card>
          <b-tab title="차트 데이터 편집" active>
            <b-row>
              <b-col cols="12">
                <EditChartDataTable
                  :chart-data="chartData.data"
                  @updateChartData="updateChartData"
                />
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="차트 편집" active>
            <b-row>
              <b-col cols="12">
                <EditChartGlobalConfig
                  :labels="chartData.data.labels"
                  :options="{ ...chartData.options }"
                  :datasets="[...chartData.data.datasets]"
                  @updateChartData="updateChartData"
                  @updateChartOption="updateChartOption"
                  @checkDuplicatedTitle="checkDuplicatedTitle"
                />
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="차트 세부 설정">
            <EditChartLocalConfig
              :datasets="[...chartData.data.datasets]"
              :options="{ ...chartData.options }"
              :labels="{ ...chartData.data.labels }"
              @updateChart="updateChart"
              @updateChartDataSets="updateChartDataSets"
            />
          </b-tab>

          <b-tab title="영역 강조">
            <EditChartAnnotations
              :options="{ ...chartData.options }"
              @updateAnnotations="updateAnnotations"
            />
          </b-tab>
        </b-tabs>
      </b-card>
    </template>

    <div class="pt-5 pb-3">
      <b-button v-if="chartData" variant="teal" @click="saveChart"> 저장하기 </b-button>
      <b-button class="ml-3" @click="closeEditChartModal"> 리스트로 돌아가기 </b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import { data_skelton_single } from '@/util/chart/chart.utils.js';
import { data_sample_list } from '@/util/chart/chart.sample.skelton.js';
import { resolveDatalabelsOptions, inferChartType } from '@/util/chart/datalabels.utils.js';
import { resolveAnnotationOptions } from '@/util/chart/annotation.utils.js';
import EditChartDataTable from '@/components/writing_report/chart_subs/EditChartDataTable.vue';
import EditChartGlobalConfig from './EditChartGlobalConfig.vue';
import EditChartLocalConfig from './EditChartLocalConfig.vue';
import EditChartAnnotations from './EditChartAnnotations.vue';
import { multiChart } from './chartSample';
import axios from 'axios';
export default {
  name: 'DefaultChart',
  components: {
    EditChartDataTable,
    EditChartGlobalConfig,
    EditChartLocalConfig,
    EditChartAnnotations
  },
  props: {
    editing_mode: { type: Boolean, default: () => false },
    e_mail: { type: String, default: '' }
  },
  computed: {
    ...mapState('report_list', ['report_code']),
    ...mapGetters('story_board', ['selected_lv2']),
    ...mapState('charts', ['chart_data', 'selected_chart_id', 'selected_chart_data'])
  },
  data() {
    return {
      multiChart,
      data_sample_list,
      // chart 인스턴스는 created()에서 non-reactive 로 선언한다 (아래 주석 참고)
      chartData: null,
      initialData: { ...data_skelton_single },
      viewSample: true,
      usable_title: false
    };
  },
  watch: {
    'chartData.options.chart_height': function (newVal, oldVal) {
      this.$nextTick(() => {
        this.renderChart();
      });
    }
  },
  created() {
    // chart.js 인스턴스는 내부에 segment helpers 등 거대한 참조 그래프를 가지므로
    // Vue reactive로 감싸면 Observer가 무한 재귀에 빠진다 (annotation plugin이 추가 ref를 박을 때 더 잘 발현).
    // data() 밖에서 instance property로 선언해 reactivity를 회피한다.
    this.chart = null;
  },
  mounted() {
    if (this.selected_chart_data !== null) {
      this.initialData = JSON.parse(JSON.stringify(this.selected_chart_data));
      this.initChart();
      return;
    }

    if (this.selected_chart_id) {
      this.initialData = {
        ...this.chart_data.find(obj => obj.chart_id === this.selected_chart_id)['form']
      };
      this.initChart();
      return;
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.chartData = null;
  },
  methods: {
    ...mapActions('story_board', ['addErtContent']),
    ...mapActions('charts', ['saveAddChart']),
    ...mapMutations('charts', ['update_edited_chart_id']),
    // saved datalabels(string enum) 보존 + ChartDataLabels 플러그인 등록을 위해
    // 렌더용 설정을 별도 객체로 만든다. 원본 this.chartData는 mutate 하지 않는다.
    _buildChartRenderConfig() {
      if (!this.chartData) return null;

      // chart.js plugin(특히 annotation)이 옵션/datasets 내부에 chart/element reference를 박는
      // 경우가 있어 Vue reactive 원본을 그대로 넘기면 Observer 무한 재귀가 발생할 수 있다.
      // chart.js에 전달할 config는 chartData와 완전히 분리된 plain 객체로 deep clone한다.
      const _cloned = JSON.parse(JSON.stringify(this.chartData));
      const _savedDatalabels = _cloned.options?.plugins?.datalabels;
      const _savedAnnotations = _cloned.options?.plugins?.annotations;
      const _chartType = inferChartType(_cloned);

      return {
        ..._cloned,
        plugins: [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            }
          },
          ChartDataLabels,
          annotationPlugin
        ],
        options: {
          ...(_cloned.options || {}),
          plugins: {
            ...(_cloned.options?.plugins || {}),
            tooltip: {
              intersect: true,
              callbacks: {
                label: context => {
                  return context.parsed.y !== undefined
                    ? `${context.dataset.label}: ${context.parsed.y
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`
                    : `${context.dataset.label}: ${context.dataset.label}: ${context.parsed
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`;
                }
              }
            },
            datalabels: resolveDatalabelsOptions(_savedDatalabels, _chartType),
            annotation: resolveAnnotationOptions(_savedAnnotations, _chartType)
          }
        }
      };
    },
    initChart() {
      this.chartData = JSON.parse(JSON.stringify(this.initialData));

      this.$nextTick(() => {
        this.renderChart();
      });
    },
    updateChartOption(options) {
      this.chartData.options = {
        ...this.chartData.options,
        ...options
      };

      this.chartData.label = this.chartData.options?.plugins?.title?.text ?? '';
      if (this.chart && this.chart.options) {
        if (this.chart) {
          this.chart.destroy();
        }
        const canvas = this.$refs.myChart;
        if (!canvas) return;
        Chart.getChart(canvas)?.destroy();
        this.chart = new Chart(canvas.getContext('2d'), this._buildChartRenderConfig());
      }
    },
    updateChartDataSets(_datasets) {
      this.chartData.data.datasets = _datasets;

      this.chart.data.datasets = this.chartData.data.datasets;
      this.chart.update();
    },
    updateChartData({ labels, datasets }) {
      this.chartData.data.labels = labels;
      this.chartData.data.datasets = datasets;
      this.chart.update();
    },
    updateChart({ data, options }) {
      if (this.chart) {
        this.chart.destroy();
      }

      this.chartData.data = { ...this.chartData.data, ...data };
      this.chartData.options = { ...this.chartData.options, ...options };

      const canvas = this.$refs.myChart;
      if (!canvas) return;
      // 같은 canvas에 stale chart 인스턴스가 남아있으면 "Canvas is already in use" 에러가 나므로 강제 정리
      Chart.getChart(canvas)?.destroy();
      const ctx = canvas.getContext('2d');
      this.chart = new Chart(ctx, this._buildChartRenderConfig());
    },
    updateAnnotations(annotations) {
      // EditChartAnnotations 탭에서 영역 강조 배열만 갱신될 때 호출.
      // options.plugins 의 다른 sub-key (datalabels 등)를 보존하기 위해 nested merge.
      this.chartData.options = {
        ...this.chartData.options,
        plugins: {
          ...(this.chartData.options?.plugins || {}),
          annotations
        }
      };

      const canvas = this.$refs.myChart;
      if (!canvas) return;
      // Chart.getChart 가 stale 인스턴스(this.chart 포함)를 일괄 정리하므로 별도 this.chart.destroy() 불필요
      Chart.getChart(canvas)?.destroy();
      this.chart = new Chart(canvas.getContext('2d'), this._buildChartRenderConfig());
    },
    renderChart() {
      const renderConfig = this._buildChartRenderConfig();
      if (!renderConfig) return;

      if (this.chart) {
        this.chart.destroy();
      }

      const canvas = this.$refs.myChart;
      if (!canvas) return;
      Chart.getChart(canvas)?.destroy();
      this.chart = new Chart(canvas.getContext('2d'), renderConfig);
    },
    applySample(idx) {
      this.chartData = JSON.parse(JSON.stringify(this.data_sample_list[idx]));

      this.$nextTick(() => {
        this.renderChart();
      });
    },
    closeEditChartModal() {
      if (this.editing_mode) {
        this.$emit('flag_modal_mode', 'chart_list');
      } else {
        this.$emit('closeEditChartModal');
      }
    },

    validateChartDataIntegrity() {
      // 저장 시 데이터 바디와 헤더/그룹명 사이의 개수 미스매치 검사
      // 미스매치 시 사용자에게 데이터 유실 경고 후 진행 여부 확인
      const labels = this.chartData?.data?.labels || [];
      const datasets = this.chartData?.data?.datasets || [];

      // 1) 데이터 바디 ↔ 테이블 헤더 개수 미스매치 검사
      const _header_count = labels.length;
      const _bad_row = datasets.findIndex(
        ds => Array.isArray(ds.data) && ds.data.length !== _header_count
      );
      if (_bad_row !== -1) {
        const _row_data_len = datasets[_bad_row].data.length;
        const _ok = window.confirm(
          `데이터 행 ${
            _bad_row + 1
          }의 셀 개수(${_row_data_len}개)가 테이블 헤더 개수(${_header_count}개)와 일치하지 않습니다.\n` +
            `저장 시 데이터가 유실될 수 있습니다. 계속 저장하시겠습니까?`
        );
        if (!_ok) return false;
      }

      // 2) 데이터 바디 ↔ 데이터 그룹명 개수 미스매치 검사 (그룹명을 일부라도 사용 중일 때만)
      const _label_count = datasets.filter(ds => ds.label && String(ds.label).trim()).length;
      if (_label_count > 0 && _label_count !== datasets.length) {
        const _ok = window.confirm(
          `데이터 그룹명 개수(${_label_count}개)가 데이터 행 개수(${datasets.length}개)와 일치하지 않습니다.\n` +
            `저장 시 데이터가 유실될 수 있습니다. 계속 저장하시겠습니까?`
        );
        if (!_ok) return false;
      }

      return true;
    },

    async saveChart() {
      if (!this.validateChartDataIntegrity()) return;

      try {
        const result = await this.saveAddChart({
          report_code: this.report_code,
          payload: this.chartData,
          chart_id: this.selected_chart_id
        });

        this.update_edited_chart_id(this.selected_chart_id);

        if (this.$route.name === 'WriteStoryBoard' && !this.selected_chart_id) {
          this.addErtContent({
            report_code: this.report_code,
            lv2_key: this.selected_lv2.key,
            id: result.chart_id,
            type: 'chart'
          });
        }
        this.$emit('closeModal', this.chartData);
        this.$emit('closeEditChartModal', this.chartData);
        this.closeEditChartModal();
      } catch (error) {
        this.$bvToast.toast('차트 저장 중 오류가 발생했습니다.', {
          title: '오류',
          variant: 'danger',
          solid: true,
          toaster: 'b-toaster-bottom-right',
          toastClass: 'custom-toast',
          appendToast: true
        });
        error.message = '[차트 저장]' + error.message;
        this.$Sentry.captureException(error);
      }
    },
    async checkDuplicatedTitle(input_title) {
      const response = await axios.get(
        `${this.$store.state.backend_host}/checkDuplicatedChartTitle?rc=${this.report_code}&title=${input_title}&chart_id=${this.selected_chart_id}`
      );
      if (response.data.result && input_title.length > 0) {
        this.usable_title = true;
        setTimeout(() => {
          this.usable_title = null;
        }, 5000);
      } else {
        this.usable_title = false;
      }
    }
  }
};
</script>

<style lang="scss">
.btn.is-active {
  background-color: rgba(0, 0, 0, 0.1);
}
</style>
